import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { useSectionParallax } from "../../hooks/useSectionParallax";
import { posthog } from "../../lib/posthog";
import kmSimulated from "../../assets/image.webp";
import kmPublished from "../../assets/image-1.webp";

// Figures come from the Evidence page's LEAP executive summary — keep in sync.
const RESULTS = [
  { value: "±0.6 mo", label: "maximum error in median overall survival" },
  { value: "HR ≈ 1.0", label: "null result correctly predicted" },
  { value: "Phase II", label: "randomized trial reproduced in silico" },
];

function CurveCard({ src, title, tag, alt, tagCls }: { src: string; title: string; tag: string; alt: string; tagCls: string }) {
  return (
    <figure className="rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 overflow-hidden bg-white dark:bg-[#0D0D10]">
      <figcaption className="px-4 py-3 border-b border-[#0A0A0F]/10 dark:border-white/10 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-[#0A0A0F] dark:text-white">{title}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${tagCls}`}>{tag}</span>
      </figcaption>
      <div className="p-3 bg-white"><img src={src} alt={alt} className="w-full rounded-md" /></div>
    </figure>
  );
}

function EvidenceText({ onEvidence }: { onEvidence: () => void }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5B21B6] dark:text-[#B9A5FF] mb-3">Evidence · LEAP trial</p>
      <h2 className="text-3xl md:text-4xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">Validated against a real trial.</h2>
      <p className="mt-4 text-base leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
        We simulated the LEAP trial (NCT01124786), a Phase II study in metastatic pancreatic cancer,
        and compared Foresight&apos;s output with the published results. It reproduced the key
        efficacy endpoints, including the trial&apos;s null result.
      </p>
      <dl className="mt-8 grid grid-cols-3 gap-4">
        {RESULTS.map(({ value, label }) => (
          <div key={label} className="border-t border-[#0A0A0F]/10 dark:border-white/10 pt-4">
            <dt className="text-xl md:text-2xl font-normal tracking-[-0.02em] text-[#0A0A0F] dark:text-white">{value}</dt>
            <dd className="mt-1 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">{label}</dd>
          </div>
        ))}
      </dl>
      <button
        onClick={() => {
          posthog.capture("cta_clicked", { cta: "Read the validation report", location: "home_evidence" });
          onEvidence();
        }}
        className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0F] dark:text-white border-b border-[#0A0A0F]/30 dark:border-white/30 hover:border-[#5B21B6] dark:hover:border-[#7C4DFF] transition-colors"
      >
        Read the validation report <ArrowRight size={14} />
      </button>
    </div>
  );
}

export function HomeEvidence({ onEvidence }: { onEvidence: () => void }) {
  const { ref, bgY, fgY } = useSectionParallax();
  return (
    <section ref={ref} className="py-20 bg-[#FAFAFA] dark:bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div style={{ y: fgY }}><EvidenceText onEvidence={onEvidence} /></motion.div>
        <motion.div style={{ y: bgY }} className="grid sm:grid-cols-2 gap-4">
          <CurveCard src={kmPublished} title="Published trial" tag="Clinical data" alt="Published LEAP trial Kaplan-Meier overall survival curve" tagCls="bg-[#1E4FD8]/10 text-[#1E4FD8] dark:bg-[#4A90D9]/15 dark:text-[#8CC0F0]" />
          <CurveCard src={kmSimulated} title="Foresight simulation" tag="In silico" alt="Foresight simulated overall survival curve for the LEAP trial" tagCls="bg-[#5B21B6]/10 text-[#5B21B6] dark:bg-[#7C4DFF]/15 dark:text-[#B9A5FF]" />
        </motion.div>
      </div>
    </section>
  );
}
